import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SuperAdminService } from '../../../services/super-admin/super-admin.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { PincodeLookupController } from '../../../common-files/pincode-lookup';

// create or edit a branch (establishment). On create the id and code are
// caller-supplied and unique; on edit both are immutable. data = { establishment }
// for edit, or null/undefined for create.
@Component({
  selector: 'app-sa-add-establishment-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './sa-add-establishment-dialog.component.html',
  styleUrls: ['./sa-add-establishment-dialog.component.scss', '../../../../common-style/dropdown.scss', '../../../../common-style/ac-dialog.scss'],
})
export class SaAddEstablishmentDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isEdit: boolean = false;
  isLoading: boolean = false;
  readonly pincodeLookup: PincodeLookupController;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<SaAddEstablishmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { establishment?: any },
    private superAdminService: SuperAdminService,
    private toastService: ToastService,
    private advanceSearchService: AdvanceSearchService
  ) {
    this.form = this.fb.group({
      establishmentId: [null, Validators.required],
      code: ['', Validators.required],
      name: ['', Validators.required],
      address: [''],
      landmark: [''],
      state: [''],
      district: [''],
      pincode: ['', Validators.pattern(/^\d{6}$/)],
      locationUrl: [''],
      status: [1],
    });
    this.pincodeLookup = new PincodeLookupController(
      this.form,
      this.advanceSearchService
    );
  }

  ngOnInit(): void {
    const est = this.data?.establishment;
    if (est) {
      this.isEdit = true;
      this.form.patchValue({
        establishmentId: est.establishmentId,
        code: est.code,
        name: est.name,
        address: est.address,
        landmark: est.landmark,
        state: est.state,
        district: est.district,
        pincode: est.pincode,
        locationUrl: est.locationUrl,
        status: est.status,
      }, { emitEvent: false });
      // id + code are immutable once the branch exists
      this.form.get('establishmentId')?.disable();
      this.form.get('code')?.disable();
    }

    this.pincodeLookup.start();
  }

  ngOnDestroy(): void {
    this.pincodeLookup.destroy();
  }

  get pincodeControl() {
    return this.pincodeLookup.pincodeControl;
  }

  get isPincodeLoading(): boolean {
    return this.pincodeLookup.isLoading;
  }

  get pincodeLookupStatus() {
    return this.pincodeLookup.status;
  }

  get pincodeLookupMessage(): string {
    return this.pincodeLookup.message;
  }

  get stateList(): string[] {
    return this.pincodeLookup.stateList;
  }

  get districtList(): string[] {
    return this.pincodeLookup.districtList;
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  submit(): void {
    const pincode = String(this.form.get('pincode')?.value ?? '');
    if (!this.pincodeLookup.isVerified(pincode)) {
      this.form.get('pincode')?.markAsTouched();
      this.toastService.error(
        this.pincodeLookupMessage || 'Enter a valid, verified 6-digit pincode.'
      );
      return;
    }

    if (this.form.invalid) {
      this.toastService.error('Branch id, code and name are required.');
      return;
    }
    this.isLoading = true;
    const payload = this.form.getRawValue();
    const est = this.data?.establishment;
    const request$ = this.isEdit
      ? this.superAdminService.updateEstablishment(est.establishmentId, payload)
      : this.superAdminService.createEstablishment(payload);
    request$.subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Saved.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to save branch.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
